import { MenuItem } from "prosemirror-menu";
import type { MarkType } from "prosemirror-model";
import { type EditorState, type Selection, TextSelection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { linkIconSpec } from "../icons";
import { UPDATE_POPOVER_CLOSE_META } from "../plugins/link-popover";
import {
  addLinkMark,
  isFormulaSelected,
  isImageSelected,
  isSelectionInCodeBlock,
  markActive,
  transformLinkUrl,
  validateLinkUrl,
} from "../utils";

import { TextField, createSelectionElement, openPopover, openPopoverForm } from "./popover";

import { t } from "i18n/i18n";
import { rollbarAndLogError } from "utils/logger";

let isCreateFormOpen = false;

/** Used by link-popover plugin to avoid opening existing-link popover over the create form */
export function isLinkCreateFormOpen(): boolean {
  return isCreateFormOpen;
}

/** Expands an empty selection to include the full link node if the cursor is inside a link mark. */
export function expandSelectionForLink(state: EditorState, markType: MarkType): Selection | null {
  if (!state.selection.empty) return state.selection;

  // If the selection is empty, we need to treat the link node as the selection
  const node = state.doc.nodeAt(state.selection.from);
  if (node && node.marks.some((mark) => mark.type === markType)) {
    const $pos = state.selection.$anchor;
    const from = $pos.pos - $pos.textOffset;
    return TextSelection.create(state.doc, from, from + node.nodeSize);
  }

  return null;
}

/** Opens the popover form for creating or editing a link. */
export function openLinkPopoverForm(
  view: EditorView,
  markType: MarkType,
  initialValue: string | null = null,
): void {
  const state = view.state;
  const selection = expandSelectionForLink(state, markType) ?? state.selection;
  const selectionEl = createSelectionElement(view, selection);

  isCreateFormOpen = true;
  openPopoverForm({
    appendTo: view.dom.closest(".prosemirror-editor-wrapper") as HTMLElement,
    selectionEl,
    title: `${t("worksheet.rich_text_editor.create_link")}:`,
    onClose: () => {
      isCreateFormOpen = false;
      view.focus();
      // After form closes, show the popover for the existing link if the cursor is still in a link
      setTimeout(() => {
        const currentState = view.state;
        if (markActive(currentState, markType) && currentState.selection.empty) {
          const close = openLinkPopoverForExistingLink(view, markType);
          // Update the plugin's close function reference
          view.dispatch(currentState.tr.setMeta(UPDATE_POPOVER_CLOSE_META, close));
        }
      }, 0);
    },
    fields: {
      href: new TextField({
        name: "href",
        value: initialValue ?? undefined,
        label: t("worksheet.rich_text_editor.link_target"),
        placeholder: "https://example.com",
        required: true,
        transform: transformLinkUrl,
        validate: validateLinkUrl,
      }),
    },
    callback(attrs) {
      const currentState = view.state;
      const currentSelection =
        expandSelectionForLink(currentState, markType) ?? currentState.selection;
      const { from, to } = currentSelection;
      view.dispatch(addLinkMark(currentState.tr, from, to, String(attrs.href)).scrollIntoView());

      view.focus();
    },
  });
}

/**
 * Opens the link popover for viewing/editing an existing link
 * @returns A function to close the popover, or null if it couldn't be opened (e.g. no link at cursor)
 */
export function openLinkPopoverForExistingLink(
  view: EditorView,
  markType: MarkType,
): (() => void) | null {
  const state = view.state;
  const selection = expandSelectionForLink(state, markType);
  if (!selection) return null;

  let hrefString: string | null = null;
  state.doc.nodesBetween(selection.from, selection.to, (node) => {
    const m = node.marks.find((mk) => mk.type === markType);
    if (m) {
      const href = m.attrs.href;
      if (typeof href === "string") hrefString = href;
    }
  });
  const link = document.createElement("a");
  if (hrefString) {
    link.href = hrefString;
    link.textContent = hrefString;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
  }

  const selectionEl = createSelectionElement(view, selection);

  let closeFn: (() => void) | null = null;

  openPopover({
    appendTo: view.dom.closest(".prosemirror-editor-wrapper") as HTMLElement,
    selectionEl,
    onClose: () => view.focus(),
    render: (close) => {
      closeFn = close;

      const createWrapper = () => {
        const wrapper = document.createElement("div");
        wrapper.className =
          "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:w-[450px]";
        return wrapper;
      };

      const addFields = (wrapper: HTMLElement) => {
        const fields = document.createElement("div");
        fields.className = "flex flex-col sm:flex-row items-start sm:items-center gap-2";
        wrapper.appendChild(fields);
        const title = document.createElement("h5");
        title.textContent = `${t("worksheet.rich_text_editor.visit_link")}:`;
        fields.appendChild(title);
        if (hrefString) {
          fields.appendChild(link);
        } else {
          rollbarAndLogError("[ProseMirrorEditor] Link mark is active but has no href attribute");
        }
      };

      const addButtons = (wrapper: HTMLElement) => {
        const buttons = document.createElement("div");
        buttons.className = "flex flex-row items-center gap-2";
        wrapper.appendChild(buttons);
        const removeButton = document.createElement("button");
        removeButton.textContent = t("common.remove");
        removeButton.onclick = () => {
          const currentState = view.state;
          const currentSelection =
            expandSelectionForLink(currentState, markType) ?? currentState.selection;
          view.dispatch(
            currentState.tr
              .removeMark(currentSelection.from, currentSelection.to, markType)
              .scrollIntoView(),
          );
          close();
        };
        buttons.appendChild(removeButton);
        const editButton = document.createElement("button");
        editButton.textContent = t("common.edit");
        editButton.onclick = () => {
          close();
          openLinkPopoverForm(view, markType, hrefString);
        };
        buttons.appendChild(editButton);
      };

      const wrapper = createWrapper();
      addFields(wrapper);
      addButtons(wrapper);

      return wrapper;
    },
  });

  return closeFn;
}

/** Creates a toolbar MenuItem that toggles the link popover or removes the link mark. */
export function linkItem(markType: MarkType) {
  return new MenuItem({
    title: t("worksheet.rich_text_editor.manage_link"),
    icon: linkIconSpec,
    active(state) {
      return markActive(state, markType);
    },
    enable(state) {
      if (isSelectionInCodeBlock(state) || isFormulaSelected(state) || isImageSelected(state))
        return false;
      if (!state.selection.empty) return true;

      // If selection is empty, only enable if the cursor is inside a link mark
      let isInLink = false;
      state.doc.nodesBetween(state.selection.from, state.selection.to, (node) => {
        if (node.marks.some((mark) => mark.type === markType)) {
          isInLink = true;
        }
      });
      return isInLink;
    },
    run(state, dispatch, view) {
      if (markActive(state, markType)) {
        // If link is active and selection is not empty, remove the link
        if (!state.selection.empty) {
          const linkSelection = expandSelectionForLink(state, markType) ?? state.selection;
          dispatch(
            state.tr.removeMark(linkSelection.from, linkSelection.to, markType).scrollIntoView(),
          );
          view.focus();
        } else {
          // If link is active but selection is empty, keep the popover open
          openLinkPopoverForExistingLink(view, markType);
        }
      } else {
        // If link is not active, open form to create link
        openLinkPopoverForm(view, markType);
      }
    },
  });
}
